import { useEffect, useState } from 'react';
import { Fill } from 'nano-slots';
import InvoiceQuickEditorDrawerPreview from 'Components/Organisms/InvoiceQuickEditorDrawer/InvoiceQuickEditorDrawerPreview';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';
import { loadSendTemplate, sendTemplate } from 'Services/invoices';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFInput, RHFTextarea } from 'Components/Organisms/HookForm';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Loading from 'Components/Atoms/Loading/Loading';

export default function InvoiceSendDrawer(props) {
    const { invoiceId, onCancel, onSubmit, onError } = props;

    const { t } = useTranslation('invoices');
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);

    const schema = yup.object({
        email: yup.string().required(t('send-email-required')).email(t('send-email-invalid')),
        title: yup.string().required(t('send-title-required')).min(1, t('send-title-invalid')),
        body: yup.string().required(t('send-body-required')).min(1, t('send-body-invalid')),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            email: '',
            title: '',
            body: '',
        },
    });

    const { handleSubmit, reset } = methods;

    useEffect(() => {
        loadTemplate();
    }, [invoiceId]);

    const loadTemplate = async () => {
        if (loading) return;

        setLoading(true);

        loadSendTemplate(invoiceId, 'XXXX')
            .then((response) => {
                reset({ title: response.title, body: response.body, email: response.email });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const submit = async (data) => {
        setProcessing(true);

        try {
            await sendTemplate(invoiceId, data.email, data.title, data.body);
            onSubmit();
        } catch (error) {
            onError(error);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <>
            <Fill name="drawer-stack-preview-desktop">
                <InvoiceQuickEditorDrawerPreview id={invoiceId} />
            </Fill>

            {loading && (
                <div className="py-6 text-center">
                    <Loading />
                </div>
            )}

            {!loading && (
                <FormProvider className="flex flex-col h-full" methods={methods} onSubmit={handleSubmit(submit)}>
                    <div className="flex flex-col gap-6 mt-6 mb-6 h-full grow max-h-none">
                        <RHFInput name="email" label={t('send-email')} />
                        <RHFInput name="title" label={t('send-concept')} />
                        <RHFTextarea
                            wrapperClassName="grow"
                            disableAutoResize
                            name="body"
                            label={t('send-body')}
                            className="h-full max-h-none min-h-36"
                        />
                    </div>

                    <div className="text-sm text-gray-500 py-4 text-center text-pretty">{t('send-email-warning')}</div>
                    <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                        <div className="flex gap-2">
                            <Button variant="default" className="grow" onClick={onCancel} testId="invoice-send.cancel">
                                {t('cancel')}
                            </Button>
                            <Button
                                isBusy={processing}
                                variant="primary"
                                className="grow"
                                type="submit"
                                testId="invoice-send.send"
                            >
                                {t('send')}
                            </Button>
                        </div>
                    </div>
                </FormProvider>
            )}
        </>
    );
}

InvoiceSendDrawer.propTypes = {
    invoiceId: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};
